import React, { useEffect, useState } from 'react';

import {
  CTA,
  InputAppearanceValue,
  ThemeProvider,
} from '@vw-marketing/us-components';
import { useFormik } from 'formik';
import * as Yup from 'yup';

import { NoTranslateTextInput } from '../../../../shared/components/no-translate-text-input';
import { ManageContactsMutation } from '../../../../shared/typings/main';
import {
  StyledManageContactsEmailFormWrapper,
  StyledManageContactsEmailButtonContainer,
} from './styled';
import { onVWBasicButtonClickWithProcessEvent } from '../../../../shared/tagging/tagging-helpers';

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const buildEmailSchema = (existingEmails: string[]) =>
  Yup.object({
    email: Yup.string()
      .matches(EMAIL_REGEX, {
        message: 'accountSettings.manage.contacts.interaction.layer.email.alerts.add.edit.error.invalid',
        excludeEmptyString: true,
      })
      .notOneOf(existingEmails, 'accountSettings.manage.contacts.interaction.layer.email.alerts.add.edit.error.duplicate'),
  });

interface ManageContactsAddEditEmailViewProps {
  trackingManager: any;
  flow: 'add' | 'edit';
  onSave: (mutation: ManageContactsMutation) => Promise<void>;
  onSuccess: () => void;
  onError: () => void;
  onChange: () => void;
  onResetChanges: () => void;
  translationHandler: (key: string) => string;
  initialEmail?: string;
  existingEmails?: string[];
}

export const ManageContactsAddEditEmailView: React.FC<ManageContactsAddEditEmailViewProps> = ({
  trackingManager,
  flow,
  onSave,
  onSuccess,
  onError,
  onChange,
  onResetChanges,
  translationHandler,
  initialEmail = '',
  existingEmails = [],
}) => {
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  const saveEmail = async (email: string) => {
    setIsSubmitting(true);
    try {
      onVWBasicButtonClickWithProcessEvent(trackingManager, {
        Name: 'Account Settings',
        StepName: `Personal Information and Security - Manage Contacts - ${flow === 'add' ? 'Add Email Address' : 'Edit Email Address'}`,
        StepNumber: '4.5',
      }, {
        name: flow === 'add' ? 'Add email address' : 'Edit email address',
      });

      await onSave({ isEdit: flow === 'edit', isRemove: false, initialValue: initialEmail, value: email });
      onSuccess();
    } catch (error) {
      console.error('There was an error saving the email', error);
      onError();
    } finally {
      setIsSubmitting(false);
    }
  };

  const formik = useFormik({
    initialValues: { email: initialEmail },
    onSubmit: async ({ email }) => {
      await saveEmail(email);
    },
    validateOnBlur: true,
    validateOnChange: true,
    validationSchema: buildEmailSchema(
      existingEmails.filter(e => e !== initialEmail)
    ),
  });

  const isButtonDisabled =
    Object.keys(formik.errors).length > 0 ||
    !formik.dirty ||
    !formik.values.email ||
    formik.isSubmitting ||
    isSubmitting;

  useEffect(() => {
    if (formik.dirty) onChange();
    else onResetChanges();
  }, [formik.values, onChange, onResetChanges, formik.dirty]);

  return (
    <ThemeProvider theme="main">
      <StyledManageContactsEmailFormWrapper>
        <form onSubmit={formik.handleSubmit}>
          <NoTranslateTextInput
            value={formik.values.email}
            onChange={formik.handleChange('email')}
            onBlur={formik.handleBlur('email')}
            name="email"
            message={
              formik.touched.email && formik.errors.email
                ? translationHandler(formik.errors.email)
                : undefined
            }
            appearance={
              formik.touched.email && formik.errors.email
                ? ('error' as InputAppearanceValue)
                : undefined
            }
            aria-label={translationHandler('accountSettings.manage.contacts.interaction.layer.email.alerts.add.edit.email.text.input.label')}
            aria-required="true"
            label={translationHandler('accountSettings.manage.contacts.interaction.layer.email.alerts.add.edit.email.text.input.label')}
          />
          <StyledManageContactsEmailButtonContainer>
            <CTA
              aria-label={flow === 'add' ? 'Add email address CTA' : 'Save email address CTA'}
              disabled={isButtonDisabled}
              emphasis="primary"
              stretchContent
              tag="button"
              type="submit"
            >
              {translationHandler(flow === 'add'
                ? 'accountSettings.manage.contacts.interaction.layer.email.alerts.add.email.cta'
                : 'accountSettings.manage.contacts.interaction.layer.email.alerts.edit.email.cta'
              )}
            </CTA>
          </StyledManageContactsEmailButtonContainer>
        </form>
      </StyledManageContactsEmailFormWrapper>
    </ThemeProvider>
  );
};
